import { Link } from "@tanstack/react-router";
import {
  CalendarDays,
  Flower2,
  Heart,
  Landmark,
  Shield,
  Sparkles,
} from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Stagger, StaggerItem } from "@project/user/components/motion";
import {
  formatUpcomingCardDate,
  formatUpcomingCardTitle,
} from "@project/lib/jain-panchang-utils";
import type { JainPanchang } from "@project/types/jain-panchang";

const CARD_ICONS = [Flower2, Landmark, Shield, Sparkles] as const;

type PanchangUpcomingListProps = {
  entries: JainPanchang[];
  onSelectEntry: (entry: JainPanchang) => void;
  labels: {
    title: string;
    details: string;
    donate: string;
  };
};

export function PanchangUpcomingList({
  entries,
  onSelectEntry,
  labels,
}: PanchangUpcomingListProps) {
  if (!entries.length) {
    return null;
  }

  return (
    <div className="mt-14">
      <div className="mb-5 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-gold" />
        <h3 className="font-serif text-xl text-foreground">{labels.title}</h3>
      </div>
      <div className="mb-5 h-px bg-border" />

      <Stagger className="-mx-1 flex gap-4 overflow-x-auto px-1 pb-2">
        {entries.map((entry, index) => {
          const Icon = CARD_ICONS[index % CARD_ICONS.length];
          const title = formatUpcomingCardTitle(entry);
          const description =
            entry.short_description ??
            entry.significance ??
            entry.recommended_activities ??
            "";

          return (
            <StaggerItem
              as="article"
              key={entry.id}
              index={index}
              from="right"
              className="card-lift border-gold/80 flex w-[17.5rem] shrink-0 flex-col overflow-hidden rounded-2xl border-t-[5px] !border-primary bg-card shadow-sm sm:w-[19rem]"
            >
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {formatUpcomingCardDate(entry)}
                  </p>
                  <div className="bg-gold/10 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-gold">
                    <Icon className="h-4 w-4" />
                  </div>
                </div>

                <h4 className="mt-3 text-base font-bold leading-snug text-foreground">
                  {title}
                </h4>

                {description ? (
                  <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {description}
                  </p>
                ) : (
                  <div className="flex-1" />
                )}

                <div className="mt-5 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => onSelectEntry(entry)}
                    className="text-sm font-semibold text-foreground transition hover:text-primary"
                  >
                    {labels.details}
                  </button>
                  <Button
                    asChild
                    size="sm"
                    className="hover:bg-gold/90 rounded-full bg-gold px-4 text-gold-foreground"
                  >
                    <Link to="/donate">
                      <Heart className="mr-1.5 h-3.5 w-3.5" />
                      {labels.donate}
                    </Link>
                  </Button>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  );
}
